import { inject, Injectable } from "@angular/core";

import { WidgetHub } from "@main/reporting.v3/core/widget-hub/models";
import { VariableRuleService } from "@main/reporting.v3/core/variables/variable-rule.service";
import { HtmlRenderingService } from "@main/reporting.v3/core/rendering/html-rendering.service";
import { WIDGET_HUB } from "@main/reporting.v3/core/widget-hub/constants/widget-hub.constants";

import { HtmlContext } from "@main/reporting.v3/widget.html/models";
import {
    WidgetHtmlAnchorProcessorService
} from "@main/reporting.v3/widget.html/services/widget-html-anchor-processor.service";
import {
    WidgetHtmlExpressionsResolverService
} from "@main/reporting.v3/widget.html/services/widget-html-expressions-resolver.service";


@Injectable()
export class WidgetHtmlContentProcessorService {

    private expressionsResolverService: WidgetHtmlExpressionsResolverService = inject(WidgetHtmlExpressionsResolverService);
    private anchorProcessorService: WidgetHtmlAnchorProcessorService = inject(WidgetHtmlAnchorProcessorService);
    private htmlRenderingService: HtmlRenderingService = inject(HtmlRenderingService);
    private variableRuleService: VariableRuleService = inject(VariableRuleService);

    private widgetHub: WidgetHub = inject(WIDGET_HUB);

    public async processContent(context: HtmlContext, html: string, container: HTMLElement): Promise<HTMLElement> {
        if (!context || !html || !container) return;

        // Sanitize, embed variables and resolve structures in the HTML code.
        html = await this.processHtmlCode(context, html);

        // Create new node tree.
        const parser: DOMParser = new DOMParser();
        const doc: Document = parser.parseFromString(html, "text/html");
        const children: ChildNode[] = Array.from(doc.body?.childNodes ?? []);

        // Move the nodes from the built document to the container.
        container.replaceChildren(...children);

        // Resolve operations and fix anchor references in the node tree.
        container = await this.processNodeTree(context, container);

        return container;
    }

    private async processNodeTree(context: HtmlContext, node: HTMLElement): Promise<HTMLElement> {
        if (!context || !node) return;

        // Reset status of all cached widgets to "non-taken".
        this.widgetHub.resetWidgetUsages();

        // Add scoped styles to the document.
        this.htmlRenderingService.insertScopedStyles(context.cssInjectionScopeId, node, context.cssRules);

        // Process anchors to route on client-side if their reference urls are internal.
        this.anchorProcessorService.processAnchorReferences(node);

        // Resolve expressions present in the node tree.
        await this.expressionsResolverService.resolveTree(context, node);

        // Clean cache from unused widgets.
        this.widgetHub.removeUnusedWidgets();

        return node;
    }

    private async processHtmlCode(context: HtmlContext, html: string): Promise<string> {
        if (!context || !html) return;

        // Remove script tags.
        html = this.htmlRenderingService.sanitizeHtml(html);

        // Substitute embedded variable names by its current values.
        html = this.variableRuleService.embedVariableValues(html, context.staticVariables);
        html = this.variableRuleService.embedVariableValues(html, context.emittedVariables);
        html = this.variableRuleService.cleanupUnmatchedVariables(html);

        // Resolve structures present in the HTML code.
        html = await this.expressionsResolverService.resolveText(context, html, { structuralOnly: true });

        return html;
    }
}